import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollText, Pencil } from "lucide-react";

export default function LegalDocsManager() {
  const [docs, setDocs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", version: "", content: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/legal/documents").then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/superadmin/legal-documents/${editing.doc_type}`, form);
      toast.success(`Documento aggiornato alla v${data.version} — notifica email inviata a ${data.notified} admin di tenant`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="legal-docs-manager" className="bg-slate-800/80 border-slate-700 mt-6">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <ScrollText className="h-5 w-5 text-sky-400" />
          <h2 className="font-display text-lg font-semibold text-slate-100">Documenti legali</h2>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Termini di Servizio e DPA mostrati agli amministratori degli istituti. A ogni salvataggio viene inviata
          un'email di notifica (silenzio-assenso) a tutti gli admin dei tenant attivi.
        </p>
        {docs === null ? <p className="text-sm text-slate-400">Caricamento...</p> : (
          <ul className="divide-y divide-slate-700">
            {docs.map((d) => (
              <li key={d.doc_type} data-testid={`legal-doc-row-${d.doc_type}`}
                className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-100">{d.title}</p>
                  <div className="text-xs text-slate-400 flex items-center flex-wrap">
                    <Badge className="bg-slate-700 text-slate-200 mr-2">v{d.version}</Badge>
                    <span>aggiornato il {d.updated_at ? new Date(d.updated_at).toLocaleString("it-IT") : "—"}</span>
                  </div>
                </div>
                <Button data-testid={`legal-doc-edit-${d.doc_type}`} variant="ghost" size="sm"
                  className="text-slate-300 hover:text-slate-100 hover:bg-slate-700"
                  onClick={() => { setEditing(d); setForm({ title: d.title, version: d.version, content: d.content }); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <Dialog open={Boolean(editing)} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent data-testid="legal-doc-edit-dialog" className="max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Modifica documento</DialogTitle>
              <DialogDescription>
                Il salvataggio aggiorna la versione e notifica via email gli admin dei tenant attivi.
              </DialogDescription>
            </DialogHeader>
            {editing && (
              <div className="space-y-4 overflow-y-auto pr-1">
                <div>
                  <Label>Titolo</Label>
                  <Input data-testid="legal-doc-title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <Label>Versione (es. 0.2, 1.0)</Label>
                  <Input data-testid="legal-doc-version" value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })} />
                </div>
                <div>
                  <Label>Contenuto completo</Label>
                  <textarea data-testid="legal-doc-content" rows={16} value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    className="w-full rounded-md border border-slate-300 p-3 text-sm font-mono2 leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#003366]" />
                </div>
                <Button data-testid="legal-doc-save" className="w-full bg-[#003366] hover:bg-[#002244]"
                  disabled={busy} onClick={save}>
                  {busy ? "Salvataggio..." : "Salva e notifica gli istituti"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
